import type { Cohort, CourseModule, Journey, ModuleJourney, ModuleStatus, NextAction, ParticipantProgress } from "./types.ts";

export function isModuleOpen(module: CourseModule, cohort: Cohort, now: Date): boolean {
  if (cohort.heldModuleIds?.includes(module.id)) return false;
  if (cohort.previewAllUntil && now < new Date(cohort.previewAllUntil)) return true;
  return now >= new Date(module.opensAt) && (cohort.unlimitedAccess || now <= new Date(cohort.accessEndsAt));
}

export function moduleStatus(module: CourseModule, cohort: Cohort, progress: ParticipantProgress, now: Date): ModuleStatus {
  if (!isModuleOpen(module, cohort, now)) return "locked";
  const completed = Math.min(progress.completedPieces[module.id] ?? 0, module.requiredPieces);
  if (completed >= module.requiredPieces) return "completed";
  if (completed > 0) return "in_progress";
  return "available";
}

export function finalFlowAvailable(cohort: Cohort, progress: ParticipantProgress, now: Date): boolean {
  const m4 = cohort.modules.find((module) => module.order === 4);
  return Boolean(m4 && moduleStatus(m4, cohort, progress, now) === "completed");
}

export function isCourseCompleted(cohort: Cohort, progress: ParticipantProgress, now: Date): boolean {
  const modulesDone = cohort.modules.every((module) => moduleStatus(module, cohort, progress, now) === "completed");
  return progress.onboardingCompleted && progress.pretestCompleted && modulesDone && progress.finalAssessmentCompleted && progress.satisfactionCompleted;
}

function nextAction(cohort: Cohort, progress: ParticipantProgress, modules: ModuleJourney[], now: Date): NextAction {
  if (!progress.onboardingCompleted) {
    return { kind: "onboarding", label: "Empezar la bienvenida", href: "/bienvenida", detail: "Conoce el recorrido y cómo guardar tu progreso." };
  }
  if (!progress.pretestCompleted) {
    return { kind: "pretest", label: "Completar el punto de partida", href: "/pretest", detail: "Una evaluación breve antes de abrir tu recorrido." };
  }
  if (finalFlowAvailable(cohort, progress, now) && (!progress.finalAssessmentCompleted || !progress.satisfactionCompleted)) {
    return { kind: "final", label: "Cerrar tu recorrido", href: "/cierre", detail: "Puedes hacerlo ahora; el momento recomendado es el encuentro de cierre." };
  }
  const current = modules.find((module) => module.status === "in_progress") ?? modules.find((module) => module.status === "available");
  if (current) {
    return { kind: "module", label: `Continuar con ${current.shortTitle}`, href: `/modulos/${current.id}`, detail: current.title };
  }
  if (isCourseCompleted(cohort, progress, now)) {
    return { kind: "complete", label: "Ver tu certificado", href: "/certificado", detail: "Has completado Familias que Acompañan." };
  }
  const nextLocked = modules.find((module) => module.status === "locked");
  return { kind: "wait", label: "Tu próximo módulo se abrirá pronto", href: "/recorrido", detail: nextLocked ? cohort.heldModuleIds?.includes(nextLocked.id) ? "Pendiente de publicación." : `Disponible desde ${formatDate(nextLocked.opensAt)}.` : "No hay nuevas acciones pendientes." };
}

export function buildJourney(cohort: Cohort, progress: ParticipantProgress, now: Date): Journey {
  const modules: ModuleJourney[] = cohort.modules.map((module) => {
    const completedPieces = Math.min(progress.completedPieces[module.id] ?? 0, module.requiredPieces);
    return {
      ...module,
      completedPieces,
      progressPercent: Math.round((completedPieces / module.requiredPieces) * 100),
      status: moduleStatus(module, cohort, progress, now)
    };
  });
  const requiredSteps = 2 + cohort.modules.length + 2;
  const completedSteps = Number(progress.onboardingCompleted) + Number(progress.pretestCompleted) +
    modules.filter((module) => module.status === "completed").length +
    Number(progress.finalAssessmentCompleted) + Number(progress.satisfactionCompleted);
  return {
    participantId: progress.participantId,
    cohortId: progress.cohortId,
    modules,
    progressPercent: Math.round((completedSteps / requiredSteps) * 100),
    finalAvailable: finalFlowAvailable(cohort, progress, now),
    courseCompleted: isCourseCompleted(cohort, progress, now),
    nextAction: nextAction(cohort, progress, modules, now)
  };
}

export function formatDate(value: string): string {
  return new Intl.DateTimeFormat("es-ES", { day: "numeric", month: "long", timeZone: "Europe/Madrid" }).format(new Date(value));
}
